import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'

// Quarterly goals living under the Phases module (migration_121).
// 3-5 goals per quarter; the quarterly-goals-review cron writes a
// Saturday-morning AI review into quarterly_goal_reviews.

export function currentQuarter(date = new Date()) {
  return `${date.getFullYear()}-Q${Math.floor(date.getMonth() / 3) + 1}`
}

export function useQuarterlyGoals() {
  const { user } = useAuth()
  const userId = user?.id || null
  const quarter = useMemo(() => currentQuarter(), [])
  const [goals, setGoals] = useState([])
  const [latestReview, setLatestReview] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const refetch = useCallback(async () => {
    if (!userId) return
    const [goalsRes, reviewRes] = await Promise.all([
      supabase
        .from('quarterly_goals')
        .select('*')
        .eq('user_id', userId)
        .eq('quarter', quarter)
        .order('sort_order')
        .order('created_at'),
      supabase
        .from('quarterly_goal_reviews')
        .select('week_start, review_text, created_at')
        .eq('user_id', userId)
        .order('week_start', { ascending: false })
        .limit(1),
    ])
    if (goalsRes.error) { setError(goalsRes.error.message); return }
    setError(null)
    setGoals(goalsRes.data || [])
    setLatestReview(reviewRes.data?.[0] || null)
  }, [userId, quarter])

  useEffect(() => {
    if (!userId) { setLoading(false); return }
    refetch().then(() => setLoading(false))
  }, [userId, refetch])

  const add = useCallback(async (title, metric) => {
    const { error: err } = await supabase
      .from('quarterly_goals')
      .insert({
        user_id: userId,
        quarter,
        title: title.trim(),
        metric: (metric || '').trim() || null,
        sort_order: goals.length,
      })
    if (err) { setError(err.message); return false }
    await refetch()
    return true
  }, [userId, quarter, goals.length, refetch])

  const setStatus = useCallback(async (id, status) => {
    const { error: err } = await supabase
      .from('quarterly_goals')
      .update({ status, updated_at: new Date().toISOString() })
      .eq('id', id)
    if (err) { setError(err.message); return false }
    await refetch()
    return true
  }, [refetch])

  const remove = useCallback(async (id) => {
    const { error: err } = await supabase.from('quarterly_goals').delete().eq('id', id)
    if (err) { setError(err.message); return false }
    await refetch()
    return true
  }, [refetch])

  return { quarter, goals, latestReview, loading, error, add, setStatus, remove, refetch }
}
